import { useQueries } from '@tanstack/react-query';
import type { Staff, WorkingHours } from '@salonomy/types';
import { chairStaff } from './salon.js';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS6 onboarding — the one piece of go-live state that isn't already loaded by SettingsPage's
 * existing hooks: whether ANY bookable chair has a weekly working-hours grid. Working hours live
 * behind a per-staff endpoint (data/working-hours.ts), so we fan out with useQueries (same idiom as
 * data/scheduling.ts → useDayCalendar) over the chairs and reduce to a single boolean the pure
 * go-live checklist (features/onboarding/goLive.ts) consumes. Cached under qk.workingHours(id) — the
 * same keys the rota editor writes — so setting hours in the wizard updates the checklist for free.
 *
 * NEW data file (partition rule): does not touch data/staff.ts / data/scheduling.ts / data/salon.ts.
 */
export function useAnyWorkingHours(staff: Staff[] | undefined): {
  anyWorkingHours: boolean;
  isLoading: boolean;
} {
  const ids = chairStaff(staff).map((s) => s.id);
  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: qk.workingHours(id),
      queryFn: async (): Promise<WorkingHours[]> =>
        unwrap(await api.GET('/v1/staff/{id}/working-hours', { params: { path: { id } } })),
    })),
  });
  return {
    anyWorkingHours: results.some((r) => (r.data?.length ?? 0) > 0),
    isLoading: results.some((r) => r.isLoading),
  };
}
